<template>
  <div class="intellectual-property-detail view">
    <Tabs>
      <template #trigger>
        <tab-controls-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :class="tabsCssVars(tab.key)"
          @click="handleTabSelected(index)"
        >
          <h5 class="title">{{ tab.title }}</h5>
        </tab-controls-item>
      </template>
      <template #controls>
        <tab-controls-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :class="tabsCssVars(tab.key)"
          @click="handleTabSelected(index)"
        >
          <h5 class="title">{{ tab.title }}</h5>
        </tab-controls-item>
      </template>

      <template #content>
        <tab-content-item
          v-if="isTabSelected('ownershipAndLicense')"
          key="ownershipAndLicense"
          :class="tabsCssVars('ownershipAndLicense')"
        >
          <cv-form>
            <div class="field-group metadata">
              <dl class="field-set type-text sls-uuid">
                <dt class="field-label">{{ t("media_object_field_label_sls_uid") }}</dt>
                <dd class="field-value">{{ uuid }}</dd>
              </dl>
              <dl class="field-set type-checkbox display-restriction">
                <dt class="field-label">
                  {{ t("media_object_field_label_display_restriction") }}
                </dt>
                <dd class="field-input">
                  <input-checkbox
                    id="displayRestriction"
                    kind="white"
                    value="checked-sls-use"
                    :selected-values="slsUseSelectedValues"
                    disabled
                  >
                    {{ t("media_object_field_label_display_restriction_publish_sls") }}
                  </input-checkbox>
                </dd>
              </dl>
            </div>

            <div class="field-group ownership">
              <dl class="field-set type-dropdown copyright-ownership mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_copyright_ownership") }}
                </dt>
                <dd class="field-value">{{ owner }}</dd>
              </dl>
              <dl class="field-set type-text copyright-owner mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_copyright_owner") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.copyRightAuthor) }}</dd>
              </dl>
              <dl class="field-set type-text owner-acknowledgement mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_acknowledgement_owner") }}
                </dt>
                <dd class="field-value">
                  {{ getColumnValue(editFormData.authorAcknowledgement) }}
                </dd>
              </dl>
              <dl class="field-set type-text author mandatory">
                <dt class="field-label">{{ t("media_object_field_label_author_creator") }}</dt>
                <dd class="field-value">{{ getColumnValue(editFormData.author) }}</dd>
              </dl>
              <dl class="field-set type-text author-attribution mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_attribution_author_creator") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.authorAttribution) }}</dd>
              </dl>
              <dl class="field-set type-dropdown resource-type mandatory">
                <dt class="field-label">{{ t("media_object_field_label_resource_type") }}</dt>
                <dd class="field-value">{{ rmsType }}</dd>
              </dl>
            </div>

            <div class="field-group license">
              <dl class="field-set type-dropdown license-type mandatory">
                <dt class="field-label">{{ t("media_object_field_label_licence_type") }}</dt>
                <dd class="field-value">{{ licenseType }}</dd>
              </dl>

              <dl
                v-if="!useMediaObject_isDisabledLicenceRemark"
                class="field-set type-text license-remarks"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_copyright_exception_details") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.exceptionDetails) }}</dd>
              </dl>

              <div class="license-duration">
                <dl
                  v-if="!useMediaObject_isDisabledLicenceDate"
                  class="field-set type-date license-start mandatory"
                >
                  <dt class="field-label">
                    {{ t("media_object_field_label_licence_start_date") }}
                  </dt>
                  <dd class="field-value">{{ getColumnValue(editFormData.startDate) }}</dd>
                </dl>
                <dl
                  v-if="!useMediaObject_isDisabledLicenceDate"
                  class="field-set type-date license-end mandatory"
                >
                  <dt class="field-label">
                    {{ t("media_object_field_label_licence_end_date") }}
                  </dt>
                  <dd class="field-value">{{ getColumnValue(editFormData.endDate) }}</dd>
                </dl>
              </div>
              <dl class="field-set type-dropdown licence-territory mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_licence_territory") }}
                </dt>
                <dd class="field-value">{{ territory }}</dd>
              </dl>
              <dl class="field-set type-dropdown single-use">
                <dt class="field-label">{{ t("media_object_field_label_single_use") }}</dt>
                <dd class="field-value">{{ singleUse }}</dd>
              </dl>
              <dl
                v-if="useMediaObject_isNonSLS"
                class="field-set type-dropdown reproduction-right"
                :class="{ mandatory: useMediaObject_isNonSLS }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_reproduction_right") }}
                </dt>
                <dd class="field-value">{{ reproduction }}</dd>
              </dl>
            </div>

            <div class="field-group rights">
              <dl class="field-set type-dropdown downloadable-rights mandatory">
                <dt class="field-label">
                  {{ t("media_object_field_label_downloadable_rights") }}
                </dt>
                <dd class="field-value">{{ downloadableRight }}</dd>
              </dl>
              <dl class="field-set type-dropdown printing-rights mandatory">
                <dt class="field-label">{{ t("media_object_field_label_printing_rights") }}</dt>
                <dd class="field-value">{{ printableRight }}</dd>
              </dl>
              <dl class="field-set type-dropdown modification-rights">
                <dt class="field-label">
                  {{ t("media_object_field_label_modification_rights") }}
                </dt>
                <dd class="field-value">{{ modificationRight }}</dd>
              </dl>
              <dl
                v-if="!useMediaObject_isDisabledModificationType"
                class="field-set type-dropdown modification-type"
                :class="{ mandatory: !useMediaObject_isMandatoryModificationType }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_type_of_modification") }}
                </dt>
                <dd class="field-value">{{ modificationType }}</dd>
              </dl>
              <dl class="field-set type-text rights-remarks">
                <dt class="field-label">
                  {{ t("media_object_field_label_modification_detail") }}
                </dt>
                <dd class="field-value">
                  {{ getColumnValue(editFormData.modificationDetails) }}
                </dd>
              </dl>
              <dl class="field-set type-text license-terms">
                <dt class="field-label">
                  {{ t("media_object_field_label_other_licence_terms") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.otherTerms) }}</dd>
              </dl>
            </div>

            <div class="field-group licensor">
              <dl class="field-set type-text license-fee">
                <dt class="field-label">
                  {{ t("media_object_field_label_commission_licience_fees") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.licenseFee) }}</dd>
              </dl>
              <dl
                class="field-set type-text licensor-name"
                :class="{ mandatory: useMediaObject_isCopyRightOwnershipLicenseMaterial }"
              >
                <dt class="field-label">{{ t("media_object_field_label_licensor_name") }}</dt>
                <dd class="field-value">{{ getColumnValue(editFormData.licensorName) }}</dd>
              </dl>
              <dl
                class="field-set type-text licensor-contact"
                :class="{ mandatory: useMediaObject_isCopyRightOwnershipLicenseMaterial }"
              >
                <dt class="field-label">{{ t("media_object_field_label_licensor_contact") }}</dt>
                <dd class="field-value">{{ getColumnValue(editFormData.licensorContact) }}</dd>
              </dl>
              <dl
                class="field-set type-text license-material-source"
                :class="{ mandatory: useMediaObject_isCopyRightOwnershipLicenseMaterial }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_source_of_licensed_material") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.licenseSource) }}</dd>
              </dl>
              <dl
                class="field-set type-text licensor-special-request"
                :class="{ mandatory: useMediaObject_isCopyRightOwnershipLicenseMaterial }"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_special_requests_from_licensor") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.licensorRequest) }}</dd>
              </dl>
              <dl class="field-set type-text other-documentation-record">
                <dt class="field-label">
                  {{ t("media_object_field_label_other_documentation_records") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.docRecord) }}</dd>
              </dl>
              <dl
                v-if="useMediaObject_isNonSLS"
                class="field-set type-text location-in-project-resources"
              >
                <dt class="field-label">
                  {{ t("media_object_field_label_location_in_project_resources") }}
                </dt>
                <dd class="field-value">{{ getColumnValue(editFormData.location) }}</dd>
              </dl>
              <dl class="field-set type-text background-ip">
                <dt class="field-label">{{ t("media_object_field_label_background_IP") }}</dt>
                <dd class="field-value">{{ getColumnValue(editFormData.backgroundIP) }}</dd>
              </dl>
            </div>
          </cv-form>
        </tab-content-item>
        <tab-content-item
          v-if="isTabSelected('availability')"
          key="availability"
          :class="tabsCssVars('availability')"
        >
          <cv-form class="field-group">
            <dl class="field-set type-text owner-group">
              <dt class="field-label">{{ t("media_object_field_label_owner_group") }}</dt>
              <dd class="field-value">{{ ownerGroup }}</dd>
            </dl>
            <dl class="field-set type-dropdown end-user-access mandatory">
              <dt class="field-label">{{ t("media_object_field_label_available_to") }}</dt>
              <dd class="field-value">{{ availability }}</dd>
            </dl>
            <dl
              v-if="!useMediaObject_isDisabledLevelSubject"
              class="field-set type-dropdown subject mandatory"
            >
              <dt class="field-label">{{ t("search_resource_detail_subject_header_label") }}</dt>
              <dd class="field-value">{{ getSubjectList }}</dd>
            </dl>
            <dl
              v-if="!useMediaObject_isDisabledLevelSubject"
              class="field-set type-dropdown level mandatory"
            >
              <dt class="field-label">{{ t("search_filter_label_level") }}</dt>
              <dd class="field-value">{{ getLevelList }}</dd>
            </dl>
          </cv-form>
        </tab-content-item>
      </template>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  AVAILABILITY,
  DOWNLOADABLE_RIGHT,
  LICENSE_TYPE,
  MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION,
  MEDIA_OBJECT_COPYRIGHT_TERRITORY,
  MODIFICATION_RIGHT,
  MODIFICATION_TYPE,
  PRINTABLE_RIGHT,
  REPRODUCTION,
  RMS_TYPE,
} from "@plugins/media/media.constant";
import { isEmpty } from "@utils/var.check";
import { MediaObjectFormData } from "@plugins/media/types.ts";
import { useMediaObject } from "@plugins/media/composables/use-media-object.ts";

// Props
interface Props {
  uuid: string;
  editFormData: MediaObjectFormData;
  ownerGroup: string;
}

const props = defineProps<Props>();

// Composables
const { t } = useI18n();

const {
  useMediaObject_isCopyRightOwnershipLicenseMaterial,
  useMediaObject_isNonSLS,
  useMediaObject_isDisabledLicenceRemark,
  useMediaObject_isDisabledLevelSubject,
  useMediaObject_isDisabledModificationType,
  useMediaObject_isMandatoryModificationType,
  useMediaObject_isDisabledLicenceDate,
} = useMediaObject(props.editFormData, {});

// Data
const tabList = ref(["ownershipAndLicense", "availability"]);
const selectedTab = ref("ownershipAndLicense");
const slsUseSelectedValues = ref(props.editFormData?.slsUse ? ["checked-sls-use"] : []);

// Computed
const tabs = computed(() => [
  {
    key: "ownershipAndLicense",
    title: t("media_object_label_ownership_licence"),
  },
  {
    key: "availability",
    title: t("media_object_label_end_user_access"),
  },
]);

const isTabSelected = computed(() => (value: string) => value === selectedTab.value);

const tabsCssVars = computed(() => (tab: string) => ({ active: isTabSelected.value(tab) }));

const getColumnValue = computed(() => (item) => (!isEmpty(item) ? item : "-"));

const owner = computed(() => {
  for (const key in MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION) {
    if (MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION[key].value === props.editFormData?.owner) {
      return t(MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION[key].label);
    }
  }
  return "-";
});

const rmsType = computed(() => {
  for (const key in RMS_TYPE) {
    if (RMS_TYPE[key].value === props.editFormData?.rmsType) {
      return t(RMS_TYPE[key].label);
    }
  }
  return "-";
});

const licenseType = computed(() => {
  for (const key in LICENSE_TYPE) {
    if (LICENSE_TYPE[key].value === props.editFormData?.licenseType) {
      return t(LICENSE_TYPE[key].label);
    }
  }
  return "-";
});

const territory = computed(() => {
  for (const key in MEDIA_OBJECT_COPYRIGHT_TERRITORY) {
    if (MEDIA_OBJECT_COPYRIGHT_TERRITORY[key].value === props.editFormData?.territory) {
      return t(MEDIA_OBJECT_COPYRIGHT_TERRITORY[key].label);
    }
  }
  return "-";
});

const singleUse = computed(() => {
  if (isEmpty(props.editFormData?.singleUseLicense)) return "-";
  return props.editFormData?.singleUseLicense ? t("yes_label") : t("no_label");
});

const reproduction = computed(() => {
  for (const key in REPRODUCTION) {
    if (REPRODUCTION[key].value === props.editFormData?.reproduction) {
      return t(REPRODUCTION[key].label);
    }
  }
  return "-";
});

const downloadableRight = computed(() => {
  for (const key in DOWNLOADABLE_RIGHT) {
    if (DOWNLOADABLE_RIGHT[key].value === props.editFormData?.downloadableRight) {
      return t(DOWNLOADABLE_RIGHT[key].label);
    }
  }
  return "-";
});

const printableRight = computed(() => {
  for (const key in PRINTABLE_RIGHT) {
    if (PRINTABLE_RIGHT[key].value === props.editFormData?.printableRight) {
      return t(PRINTABLE_RIGHT[key].label);
    }
  }
  return "-";
});

const modificationRight = computed(() => {
  for (const key in MODIFICATION_RIGHT) {
    if (MODIFICATION_RIGHT[key].value === props.editFormData?.modificationRight) {
      return t(MODIFICATION_RIGHT[key].label);
    }
  }
  return "-";
});

const modificationType = computed(() => {
  const modificationTypeList = props.editFormData?.modificationType ?? [];
  const modificationLabelMap = Object.fromEntries(
    Object.values(MODIFICATION_TYPE).map(({ value, label }) => [value, label]),
  );
  const modificationTypeName =
    modificationTypeList.length === 0
      ? "-"
      : modificationTypeList
          .map((item: string) => t(modificationLabelMap[item]) || item)
          .join(", ");
  return modificationTypeName;
});

const availability = computed(() => {
  for (const key in AVAILABILITY) {
    if (AVAILABILITY[key].value === props.editFormData?.availability) {
      return t(AVAILABILITY[key].label);
    }
  }
  return "-";
});

const getSubjectList = computed(() => {
  const subjectList = props.editFormData?.subjects ?? [];
  return subjectList.length === 0 ? "-" : subjectList.map((item) => item.subjectName).join(", ");
});

const getLevelList = computed(() => {
  const levelList = props.editFormData?.levelAndStreams ?? [];
  return levelList.length === 0 ? "-" : levelList.map((item) => item.displayName).join(", ");
});

// Methods
const handleTabSelected = (index: number) => {
  selectedTab.value = tabList.value[index];
};
</script>
